/**
 * PDF preview panel for the Candid auth letter in the RPA status modal.
 *
 * The PDFium binary is loaded from `public/pdfium.wasm` (written by
 * `scripts/copy-pdfium-wasm.cjs`). The renderer's own default points at
 * jsDelivr, which the app's CSP `connect-src` blocks — leaving the viewer
 * stuck on "Loading PDF…" — so `wasmUrl` must stay set.
 *
 * `worker` is off deliberately. In worker mode the engine object is handed
 * back before the worker finishes loading the wasm, and a failed load posts a
 * `wasmError` message the host drops (it carries no request id), so every
 * later call queues behind a handshake that never completes and the viewer
 * spins forever with nothing logged. The direct engine awaits the wasm, so a
 * failure reaches `onError` / `renderError` instead of hanging.
 *
 * @module candidRpaAuthLetterPdfPanel
 */
import { PdfRenderer } from "@risalabs_frontend_org/oasis-ui-kit";
import { logError } from "../../../utils/customLogger";

/** Self-hosted PDFium binary served from `public/`. */
const PDFIUM_WASM_URL = "/pdfium.wasm";

/** Props for {@link CandidRpaAuthLetterPdfPanel}. */
export interface CandidRpaAuthLetterPdfPanelProps {
  /** Firebase or signed URL for the auth-letter PDF. */
  src: string;
  /** Document label shown in the panel header. */
  name?: string;
  /** Timestamp or relative time shown in the panel header. */
  time?: string;
}

/**
 * Renders the auth-letter PDF with a name/time header for the CMM tab.
 *
 * @param props.src - PDF source URL
 * @param props.name - Header document name
 * @param props.time - Header timestamp label
 * @returns PDF viewer panel
 */
const CandidRpaAuthLetterPdfPanel = ({
  src,
  name = "Name",
  time = "Time",
}: CandidRpaAuthLetterPdfPanelProps) => {
  return (
    <div className="pdf-renderer-for-candid-auth-letter flex h-full flex-col overflow-hidden">
      <div className="pdf-renderer-header flex items-center justify-between border-b border-primaryGray-14 px-4 py-2 text-tiny font-semibold">
        <div className="pdf-renderer-name">{name}</div>
        <div className="pdf-renderer-time">{time}</div>
      </div>
      <div className="pdf-renderer-content min-h-0 flex-1">
        <PdfRenderer
          src={src}
          wasmUrl={PDFIUM_WASM_URL}
          worker={false}
          className="h-full w-full !rounded-none !border-0"
          height="100%"
          width="100%"
          onError={(error) =>
            logError(
              error ?? new Error("Unknown PDF renderer error"),
              "Candid auth letter PDF failed to render",
            )
          }
          renderError={(error) => (
            <div className="flex h-full w-full items-center justify-center p-4 text-sm text-red-600">
              {error?.message || "Unable to load this document."}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default CandidRpaAuthLetterPdfPanel;
